import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";
import { MessageCircle, ArrowRight, ShieldCheck, ChevronDown } from "lucide-react";
import { COUNTRIES } from "../lib/countries";
import { useCountryAutoDetect } from "../lib/useCountryAutoDetect";

/**
 * Website → WhatsApp funnel modal.
 *
 * Captures name + country + city + age + chief complaint + phone (+ optional
 * email) BEFORE we deep-link the visitor into the NeuroOpinion business
 * WhatsApp thread. Only email is optional — the rest are required so the
 * doctor/advisor has enough context on first message.
 *
 * On submit:
 *   1. POST /api/leads/whatsapp-intake creates a lead with the full profile
 *   2. Backend returns a pre-filled wa.me URL
 *   3. We open the URL in a new tab so WhatsApp launches with the message ready
 */
const COUNTRIES_LOCAL_DUP = null; // country list moved to /lib/countries.js — imported above

export default function WhatsAppIntakeModal({ open, onClose, source = "landing_wa" }) {
  const [form, setForm] = useState({
    name: "", country: "IN", phone: "", email: "", chief_complaint: "", age: "", city: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-detect country: IP → browser locale → India fallback
  const { country: detectedCountry } = useCountryAutoDetect("IN");
  useEffect(() => {
    if (detectedCountry && detectedCountry !== form.country) {
      setForm((f) => ({ ...f, country: detectedCountry }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectedCountry]);

  const country = COUNTRIES.find((c) => c.iso === form.country) || COUNTRIES[0];

  const submit = async () => {
    if (form.name.trim().length < 2) { toast.error("Please enter your name"); return; }
    if (form.chief_complaint.trim().length < 3) { toast.error("Please describe the main symptom"); return; }
    if (!form.age || Number(form.age) < 1 || Number(form.age) > 120) {
      toast.error("Please enter a valid age"); return;
    }
    if (form.city.trim().length < 2) { toast.error("Please enter your city"); return; }
    const phoneClean = form.phone.replace(/[\s\-()]/g, "");
    if (!/^\+?\d{7,15}$/.test(phoneClean)) {
      toast.error("Please enter a valid mobile number"); return;
    }
    if (form.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      toast.error("Enter a valid email address (or leave blank)"); return;
    }
    // Ensure phone carries the country dial code (patient may type local digits only).
    const phoneIntl = phoneClean.startsWith("+")
      ? phoneClean
      : (phoneClean.startsWith(country.dial.replace("+", ""))
          ? `+${phoneClean}`
          : `${country.dial}${phoneClean}`);

    setBusy(true);
    try {
      const r = await api.post("/leads/whatsapp-intake", {
        name: form.name.trim(),
        phone: phoneIntl,
        email: form.email.trim() || null,
        chief_complaint: form.chief_complaint.trim(),
        age: Number(form.age),
        city: form.city.trim(),
        country: form.country,
        source,
      });
      const url = r.data?.whatsapp_url;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
        toast.success("Opening WhatsApp — say hi and our team will assist 🙏");
        onClose?.();
      } else {
        toast.success("Got it! Our team will reach out shortly.");
        onClose?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open WhatsApp. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="wa-intake-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <MessageCircle className="w-5 h-5 text-green-600"/> Continue on WhatsApp
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Tell us briefly what you&apos;re dealing with — we&apos;ll open WhatsApp with the message pre-filled so you don&apos;t have to type it again.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 mt-1">
          <div>
            <Label className="text-slate-700">Your name *</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ramesh Kumar" className="mt-1" data-testid="wa-name"/>
          </div>
          <div>
            <Label className="text-slate-700">Main symptom / concern *</Label>
            <Textarea value={form.chief_complaint} onChange={(e) => set("chief_complaint", e.target.value.slice(0, 400))} rows={3}
                      placeholder="e.g. recurring headaches for 2 months, MRI shows a small lesion" className="mt-1" data-testid="wa-complaint"/>
            <div className="text-[10px] text-slate-400 text-right mt-0.5">{form.chief_complaint.length}/400</div>
          </div>
          <div>
            <Label className="text-slate-700">Country *</Label>
            <div className="relative mt-1">
              <select
                value={form.country}
                onChange={(e) => set("country", e.target.value)}
                data-testid="wa-country"
                className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 pr-9 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.iso} value={c.iso}>{c.name} ({c.dial})</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-700">Age *</Label>
              <Input value={form.age} onChange={(e) => set("age", e.target.value.replace(/\D/g, ""))} placeholder="42" className="mt-1" inputMode="numeric" data-testid="wa-age"/>
            </div>
            <div>
              <Label className="text-slate-700">City *</Label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Hyderabad" className="mt-1" data-testid="wa-city"/>
            </div>
          </div>
          <div>
            <Label className="text-slate-700">Mobile number *</Label>
            <div className="flex mt-1">
              <span className="inline-flex items-center px-2.5 h-10 rounded-l-md border border-r-0 border-slate-300 bg-slate-50 text-slate-600 text-sm font-medium">
                {country.dial}
              </span>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="9xxxxxxxxx" className="rounded-l-none" inputMode="tel" data-testid="wa-phone"/>
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5">We&apos;ll call this number if you don&apos;t reply on WhatsApp.</div>
          </div>
          <div>
            <Label className="text-slate-700">Email (optional)</Label>
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" className="mt-1" inputMode="email" data-testid="wa-email"/>
          </div>
        </div>
        <DialogFooter className="mt-2 sm:justify-between flex-wrap gap-2">
          <div className="text-[11px] text-slate-500 flex items-center gap-1.5 self-center">
            <ShieldCheck className="w-3.5 h-3.5"/> Your details are confidential and never shared without consent.
          </div>
          <Button onClick={submit} disabled={busy} className="bg-green-600 hover:bg-green-700" data-testid="wa-intake-submit">
            {busy ? "Opening…" : "Open WhatsApp"} <ArrowRight className="w-4 h-4 ml-2"/>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
